import classcat from "classcat";
import React from "react";

import {
  Button,
  IconArrowDownCircle,
  IconX,
} from "@touchtech/web-baselayer-ui";

import WidgetProductItem from "@is/features/ContentView/WidgetItem/WidgetProductItem";
import { WidgetItem as WidgetItemType, WidgetType } from "@is/types";

import css from "./ContentWidgetItem.module.css";
import useManageContentWidgetItem from "./hooks/useManageContentWidgetItem";

type Props = {
  widget: WidgetItemType;
  sectionId: string;
  contentItemId?: string;
  widgetType?: WidgetType;
  maxPageItemsLimit?: number;
};

const ContentWidgetItem = ({
  widget,
  contentItemId,
  widgetType,
  sectionId,
  maxPageItemsLimit,
}: Props) => {
  const {
    items,
    showViewMoreButton,
    handleViewMoreClick,
    widgetRef,
    handleItemClick,
    getAnimationDelayIndex,
    t,
    noMoreItemsToLoad,
    quickAddToBasketItem,
    handleQuickAddToBasket,
    handleQuickAddToBasketRemoveItem,
    handleQuickAddToBasketLayerClick,
    handleCloseWidget,
    notOnlyItemInSection,
    widgetTitle,
  } = useManageContentWidgetItem({
    widget,
    sectionId,
    contentItemId,
    widgetType,
    maxPageItemsLimit,
  });

  if (!items.length) {
    return null;
  }

  return (
    <div
      className={classcat([
        css.container,
        css.closableContainer,
        { [css.expanded]: !widget.isClosed },
      ])}
      ref={widgetRef}
    >
      <div>
        {notOnlyItemInSection && (
          <div className={css.closeButton}>
            <Button
              onClick={handleCloseWidget}
              className={classcat(["text-label-4xl"])}
              sizing="xl"
              variant="tertiary"
            >
              <IconX />
            </Button>
          </div>
        )}
        <div className={css.titleContainer}>
          <div className={classcat(["text-headline-3xl", css.title])}>
            <div>{widgetTitle}</div>
            {widget.customSubtitle && (
              <div className={css.subtitle}>{widget.customSubtitle}</div>
            )}
          </div>
        </div>

        <div className={css.itemsContainer}>
          {items?.map((item, index) => (
            <WidgetProductItem
              onClick={handleItemClick}
              key={`${widget.id}-${item.id}`}
              item={item}
              animationDelayIndex={getAnimationDelayIndex(index)}
              quickAddToBasketItem={
                quickAddToBasketItem?.widgetProductItemId ===
                item?.widgetProductItemId
                  ? quickAddToBasketItem
                  : null
              }
              onQuickAddToBasketRemoveItem={handleQuickAddToBasketRemoveItem}
              quickAddToBasketMode={!!quickAddToBasketItem}
              onQuickAddToBasket={handleQuickAddToBasket}
              onQuickAddToBasketLayerClick={handleQuickAddToBasketLayerClick}
              widgetType={widget.type}
            />
          ))}
        </div>
        {showViewMoreButton && (
          <div className={css.viewMoreButtonContainer}>
            <button
              onClick={handleViewMoreClick}
              className={css.viewMoreButton}
            >
              <IconArrowDownCircle />
              <span>{t("View more")}</span>
            </button>
          </div>
        )}
        {noMoreItemsToLoad && (
          <div
            className={classcat([
              css.viewMoreButtonContainer,
              css.viewMoreButton,
            ])}
          >
            <span>{t("No more items to load")}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentWidgetItem;
